import React from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { Theme, ThemeProps } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { AlternativeTourInformation } from '@model/tours';
import { Amount, Icons, State, TripTypes } from '@model/common';
import { setSearchParams } from '@state/slices/search/tours';
import { TestId } from '@components/test-ids';
import { useI18NextContext } from '@components/hooks';
import { Button, ButtonSize, ButtonVariant, ButtonColor } from '@components/material-ui';
import { Snackbar } from '@components/common/snackbar';
import { TourLink } from '@components/common/next-link/tour-link';

/* ***************** *
 *       Types       *
 * ***************** */
export interface AlternativeTourPanelProps {
  theme?: Theme;
  testId?: string;
  alternativeTour: AlternativeTourInformation;
  fromTripType: TripTypes;
  adults?: number;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled(Snackbar)(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.large,
  p: {
    margin: 0
  }
}));

const Inner = styled.div({
  [mq.small]: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'space-between'
  }
});

const PrivateTourTitle = styled.p({
  fontWeight: 'bold'
});

const CtaButton: any = styled(Button)(({ theme }: ThemeProps) => ({
  marginLeft: 0,
  marginTop: theme.custom.spacing.large,
  whiteSpace: 'nowrap',
  [mq.small]: {
    marginLeft: theme.custom.spacing.large,
    marginTop: 0
  },
  ['.MuiButtonBase-root']: {
    borderColor: theme.custom.colors.group2.base,
    borderRadius: 4,
    borderWidth: 1,
    ['&:hover']: {
      borderWidth: 1,
      backgroundColor: theme.custom.colors.group1.light
    }
  }
}));

interface AlternativeTourRulesProps {
  adults?: number;
}
export const AlternativeTourRules = {
  [TripTypes.SOLO_TOURS]: ({ adults }: AlternativeTourRulesProps) => {
    return adults === 1;
  },
  [TripTypes.PRIVATE_TOURS]: ({ adults }: AlternativeTourRulesProps) => {
    return adults !== 1;
  }
};

/* ********************************** *
 *    AlternativeTourPanelComponent   *
 * ********************************** */
export const AlternativeTourPanelComponent = ({
  testId,
  alternativeTour,
  fromTripType,
  adults
}: AlternativeTourPanelProps) => {
  const t: any = useI18NextContext();
  const dispatch = useDispatch();
  const { tripType } = alternativeTour;

  const displayRule = AlternativeTourRules[tripType];
  if (displayRule && !displayRule({ adults })) {
    return null;
  }

  const getAlternativeTourOption = (alternativeTour: AlternativeTourInformation) => {
    const { diff, price, groupPrice, path, tripType } = alternativeTour;
    const toTripType = tripType || TripTypes.ESCORTED_TOURS;
    const isPrivateTour = toTripType === TripTypes.PRIVATE_TOURS;
    const fromSoloTour = fromTripType === TripTypes.SOLO_TOURS;
    const headerKey = `product__${fromTripType}--${toTripType}-header`;
    const textKey = `product__${fromTripType}--${toTripType}-text`;
    const ctaLabelKey = `product__${fromTripType}--${toTripType}-cta`;

    const amount = isPrivateTour ? diff.amount : groupPrice ? groupPrice.amount : price.amount;

    const onLinkClick = () => {
      if (fromSoloTour) {
        dispatch(
          setSearchParams({
            occupancy: [{ adults: 1, children: [] }]
          })
        );
      }
    };

    return (
      <Inner>
        <div>
          <PrivateTourTitle>
            {t(headerKey, {
              price: new Amount(amount, price.currency.code).format(),
              diffPrice: new Amount(diff.amount, price.currency.code).format()
            })}
          </PrivateTourTitle>
          <p>{t(textKey)}</p>
        </div>
        <TourLink
          path={path}
          tripType={toTripType}
          onMouseDown={onLinkClick}
          search={fromSoloTour ? { adults: 1 } : undefined}
        >
          <CtaButton
            label={t(ctaLabelKey)}
            color={ButtonColor.PRIMARY}
            size={ButtonSize.SMALL}
            variant={ButtonVariant.OUTLINED}
          />
        </TourLink>
      </Inner>
    );
  };

  return (
    <Container
      testId={testId || TestId.alternativeTourPanel.main}
      icon={Icons.BOOKING}
      state={State.INFO}
      renderText={getAlternativeTourOption(alternativeTour)}
    />
  );
};

export const AlternativeTourPanel = withTheme(AlternativeTourPanelComponent);
